import { Component, Input, OnDestroy, OnInit } from "@angular/core"
import { Router } from "@angular/router"
import { ISchool, ISchoolDistrict, IState } from "../../../models/userDataPrivacy.model"
import { BehaviorSubject, Subject } from "rxjs"
import { map, takeUntil, tap } from "rxjs/operators"
import { UserDataPrivacyService } from "../../../services/user-data-privacy.service"
import { ApiResponse } from "../../../models/apiResponse.model"

enum LABEL_TEXT {
    AWAITING_ASSIGNMENTS = "Go To Assignments",
    ASSIGNED_ASSIGNMENTS = "Go To Assignments",
    NEW_LESSON_BUILDER = "Build Your First Lesson",
}

enum NAVIGATE_TO_URL {
    AWAITING_ASSIGNMENTS = "/tools/assignments/tab/awaiting/",
    ASSIGNED_ASSIGNMENTS = "/tools/assignments/",
    NEW_LESSON_BUILDER = "/tools/lessonbuilder/new/",
}

enum NAVIGATE_TO {
    AWAITING_ASSIGNMENTS = "AWAITING_ASSIGNMENTS",
    ASSIGNED_ASSIGNMENTS = "ASSIGNED_ASSIGNMENTS",
    NEW_LESSON_BUILDER = "NEW_LESSON_BUILDER",
}

const NOT_SUPPLIED = "Not supplied"

@Component({
    selector: "app-data-privacy-completed",
    templateUrl: "./data-privacy-completed.component.html",
    styleUrls: ["./data-privacy-completed.component.scss"],
    standalone: false,
})
export class DataPrivacyCompletedComponent implements OnDestroy, OnInit {
    label = LABEL_TEXT.AWAITING_ASSIGNMENTS

    destroyed$ = new Subject<void>()

    schoolStateName: BehaviorSubject<string> = new BehaviorSubject<string>("NOT_SUPPLIED")
    schoolDistrictName = ""
    schoolName = ""

    dataPrivacy

    navUrl = NAVIGATE_TO_URL[NAVIGATE_TO.AWAITING_ASSIGNMENTS]

    getSchoolStateName$ = this.userDataPrivacyService.getStates().pipe(
        takeUntil(this.destroyed$),
        map((apiResponse: ApiResponse<IState>) => apiResponse.results),
        tap((states: IState[]): void => {
            this.schoolStateName.next(
                states.find(
                    (state: IState): boolean =>
                        state.state_location ===
                        (this.dataPrivacy?.school_district as ISchoolDistrict).state_location
                )?.name
            )
        })
    )

    @Input()
    set assignments({ assignments, dataPrivacy }) {
        this.dataPrivacy = dataPrivacy
        const navigateTo = this.getNavigateTo(assignments)

        this.label = LABEL_TEXT[navigateTo]
        this.navUrl = NAVIGATE_TO_URL[navigateTo]

        this.schoolDistrictName = Boolean(dataPrivacy?.school_district?.district_name)
            ? dataPrivacy.school_district.district_name
            : NOT_SUPPLIED

        this.schoolName = Boolean(dataPrivacy?.school_name) ? dataPrivacy.school_name : NOT_SUPPLIED
    }

    constructor(
        public router: Router,
        public userDataPrivacyService: UserDataPrivacyService
    ) {}

    ngOnInit() {
        this.getSchoolStateName$.subscribe()
    }

    /**
     * Returns where to redirect the user based on assignments type
     * @param assignments
     * @private
     */
    private getNavigateTo(assignments: { awaiting: number; assigned: number }): NAVIGATE_TO {
        if (assignments.awaiting > 0) return NAVIGATE_TO.AWAITING_ASSIGNMENTS
        if (assignments.assigned > 0) return NAVIGATE_TO.ASSIGNED_ASSIGNMENTS
        return NAVIGATE_TO.NEW_LESSON_BUILDER
    }

    public changeSchool(school: ISchool): void {
        this.schoolName = school.name
    }
    public changeSchoolDistrict(schoolDistrict: ISchoolDistrict): void {
        this.schoolDistrictName = schoolDistrict.district_name
    }

    public changeSchoolState(schoolState: IState): void {
        this.schoolStateName.next(schoolState?.name ? schoolState.name : "NOT_SUPPLIED")
    }

    ngOnDestroy() {
        this.destroyed$.next()
        this.destroyed$.complete()
    }
}
